import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
import { notificationInboxEntityTypeEnum, notificationInboxSeverityEnum, } from './notification.js';
/**
 * Shared wire schemas for the admin WebSocket gateway — Wave 2.34 BE-2
 * (TASK-106).
 *
 * Transport: socket.io namespace `/admin` (see `apps/api/src/websockets/`).
 * Auth: JWT supplied in `client.handshake.auth.token`; verified server-side
 * against `JWT_ACCESS_SECRET`. Same access token the admin SPA uses for HTTP.
 *
 * Rooms (server-side; clients don't `subscribe` to them — gateway auto-joins
 * on connect based on the validated JWT claims):
 *   - `user:${userId}`        — recipient-scoped events (notifications.new)
 *   - `account:${accountId}`  — tenant-scoped events (account.events)
 *   - `system:alerts`         — super_admin-only (cron failures, queue depth)
 *
 * For v0.1.0 only ONE producer is wired end-to-end (notifications.new on
 * inbox INSERT via `AdminEventsService.notifyUser`). The account / system
 * envelopes are defined here so the FE can subscribe to all three event
 * types without a follow-up shared-package release.
 *
 * Event names are kept stable as DOT-separated lowercase tokens
 * (`<domain>.<verb>`) — chosen for grep-ability and to match the analyst
 * topic taxonomy in TASK-106. They are wire identifiers; do NOT rename
 * without a coordinated FE+BE migration.
 */
// ---------------------------------------------------------------------------
// Event names — single source of truth
// ---------------------------------------------------------------------------
export const WS_EVENT_NOTIFICATIONS_NEW = 'notifications.new';
export const WS_EVENT_ACCOUNT_EVENT = 'account.events';
export const WS_EVENT_SYSTEM_ALERT = 'system.alert';
/**
 * Wave 2.66 BE-2 polish (TASK-042-followup) — emitted into
 * `user:${submittedBy}` every 1–2 seconds while a bulk job is running.
 * The FE replaces poll-based progress queries with a socket subscription
 * once the wire is alive on both sides.
 */
export const WS_EVENT_BULK_PROGRESS = 'tracks.bulk_progress';
/**
 * Final terminal event — `status` carries the canonical bulk_jobs status
 * ('completed' / 'failed' / 'cancelled'). FE invalidates queries and
 * surfaces the toast.
 */
export const WS_EVENT_BULK_COMPLETE = 'tracks.bulk_complete';
// ---------------------------------------------------------------------------
// notifications.new — emitted into `user:${recipient_user_id}` whenever a new
// row lands in `customers.notifications_inbox`. Mirrors the subset of fields
// the bell-icon drawer needs to render WITHOUT another HTTP round-trip.
// ---------------------------------------------------------------------------
const TITLE_MAX = 255;
export const notificationNewEventSchema = z.object({
    id: uuidSchema,
    title: z.string().min(1).max(TITLE_MAX),
    severity: notificationInboxSeverityEnum,
    entity_type: notificationInboxEntityTypeEnum.nullable(),
    entity_id: uuidSchema.nullable(),
    created_at: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// account.events — emitted into `account:${account_id}` whenever an
// account-level state change occurs (subscription status, venue add/remove,
// etc.). `event_type` is a free-form discriminator owned by the producer
// service; `payload` carries the unstructured detail.
//
// Schema is intentionally loose: the FE switches on `event_type` and decides
// what to render. Tightening to a discriminated union is a follow-up once
// the producer surface stabilises.
// ---------------------------------------------------------------------------
const EVENT_TYPE_MAX = 64;
export const accountEventSchema = z.object({
    event_type: z.string().min(1).max(EVENT_TYPE_MAX),
    payload: z.record(z.unknown()),
    occurred_at: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// system.alert — super_admin-only firehose for ops-grade signals (queue
// backlog, cron failures, Sentry rate spikes). Emitted into `system:alerts`.
// ---------------------------------------------------------------------------
const ALERT_TITLE_MAX = 255;
const ALERT_BODY_MAX = 4_000;
export const systemAlertSeverityEnum = z.enum(['info', 'warning', 'critical']);
export const systemAlertEventSchema = z.object({
    severity: systemAlertSeverityEnum,
    title: z.string().min(1).max(ALERT_TITLE_MAX),
    body: z.string().min(1).max(ALERT_BODY_MAX),
    occurred_at: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// tracks.bulk_progress / tracks.bulk_complete — Wave 2.66 BE-2 polish
// (TASK-042-followup). The FE renders a live progress bar instead of polling
// `GET /admin/bulk/jobs/:id`.
// ---------------------------------------------------------------------------
export const bulkProgressEventSchema = z.object({
    job_id: uuidSchema,
    processed: z.number().int().min(0),
    failed: z.number().int().min(0),
    total: z.number().int().min(0),
    /** 0..100 derived from `processed / total`; -1 when `total === 0`. */
    percent: z.number().int().min(-1).max(100),
});
export const bulkCompleteEventSchema = z.object({
    job_id: uuidSchema,
    status: z.enum(['completed', 'failed', 'cancelled']),
    processed: z.number().int().min(0),
    failed: z.number().int().min(0),
    total: z.number().int().min(0),
    error_message: z.string().nullable(),
});
//# sourceMappingURL=ws-events.js.map